import { Datetime, Dayts, DaytsFormatEnum, getClasses, getResultFromState, zeroPadFlightNumber } from '@/utils';
import { InvoiceFormatEnum, InvoiceTrip, InvoiceTripCombineTypeEnum, InvoiceTripTypeEnum } from '@/models/gen/graphql';
import { InvoicePreviewTableProvider, useInvoicePreviewTable } from '@/pages/InvoicePreview/InvoicePreviewTable';
import { RefObject, forwardRef } from 'react';

import CrewIdCell from '@/pages/InvoicePreview/components/CrewIdCell';
import { DATE_FE_FORMAT, INVALID_DATE_STRING } from '@/constants';
import { PreviewTrip } from '@/pages/InvoicePreview';
import RateCell from '@/common/RateCell';
import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import SimpleTableRow from '@/components/SimpleTable/SimpleTableRow';
import { formatDollars } from '../../../utils/numbers';

export type SelectedRowAndFormat = {
  row: PreviewTrip;
  format: InvoiceFormatEnum;
  payerProviderId: string;
  combineIds: string[];
  filteredTypes: InvoiceTripTypeEnum[];
  hasDiscounts?: number;
};

const ILLEGAL_COMBINE_TYPES = [InvoiceTripCombineTypeEnum.Illegal, InvoiceTripCombineTypeEnum.IllegalOverCapacity];

const InvoicePreviewRow = ({ id }: { id: string }, ref: RefObject<HTMLDivElement>): JSX.Element => {
  const [{ row, format, combineIds, filteredTypes, hasDiscounts }, setPreview, { onRefetch }] =
    useInvoicePreviewTable<SelectedRowAndFormat>(
      ({ trips, format, payerProviderId, combineIds, filteredTypes, hasDiscounts }: InvoicePreviewTableProvider): SelectedRowAndFormat => ({
        row: trips.get(id),
        format,
        payerProviderId,
        combineIds,
        filteredTypes,
        hasDiscounts,
      })
    );
  const { count = 0, subtotal, trip = {} as InvoiceTrip } = row || {};

  const setRow = (update: PreviewTrip): void => {
    setPreview(
      (current: InvoicePreviewTableProvider): InvoicePreviewTableProvider => ({
        ...current,
        trips: new Map(current.trips.set(id, getResultFromState(update, current.trips.get(id)))),
      })
    );
    onRefetch();
  };

  const combineIndex = combineIds.findIndex((id) => id === trip?.combineId);
  const isIllegalCombine = ILLEGAL_COMBINE_TYPES.includes(trip?.combineType);
  const getCombineClass = (index: number): 'COMBINE-LIGHT' | 'COMBINE-DARK' => ((index + 1) % 2 === 0 ? 'COMBINE-DARK' : 'COMBINE-LIGHT');

  return (
    <SimpleTableRow
      id={id}
      className={getClasses(
        'InvoicePreviewRow',
        subtotal !== undefined ? 'SUBTOTAL' : undefined,
        subtotal === undefined && filteredTypes.length > 0 && !filteredTypes.includes(trip.type) ? 'IGNORED' : undefined,
        subtotal === undefined && !isIllegalCombine && combineIndex >= 0 && hasDiscounts === 1 ? getCombineClass(combineIndex) : undefined
      )}
      data={trip}
      ref={ref}
    >
      {filteredTypes.length > 0 && !filteredTypes.includes(trip.type) ? (
        <SimpleTableCell name="INDEX">
          {`Combined with a trip from a scheduled (${trip.type})`}{' '}
          <span className="fw-bold">{`${new Dayts(trip.scheduled).formatAs(DaytsFormatEnum.DATE_FE_SHORT)} ${new Dayts(trip.scheduled).formatAs(DaytsFormatEnum.TIME)}   ${trip.servicerIataAirlineCode}${trip.flightNumber}`}</span>{' '}
        </SimpleTableCell>
      ) : (
        <SimpleTableCell name="INDEX">{count}</SimpleTableCell>
      )}
      <SimpleTableCell name="TYPE">{trip?.type || '--'}</SimpleTableCell>
      <SimpleTableCell name="SCHEDULED_DATE">
        {trip?.scheduled?.trim?.() ? new Datetime(trip?.scheduled).format(DATE_FE_FORMAT) : ' '}
      </SimpleTableCell>
      <SimpleTableCell name="SCHEDULED_TIME">{trip?.scheduled?.trim?.() ? new Datetime(trip?.scheduled).time : ' '}</SimpleTableCell>
      <SimpleTableCell name="SCHEDULED_TIME">
        {trip?.importTime?.trim?.()
          ? trip?.importTime?.trim?.() === INVALID_DATE_STRING
            ? new Datetime(trip?.scheduled).time
            : new Datetime(trip?.importTime).time
          : '--'}
      </SimpleTableCell>
      <SimpleTableCell name="AIRPORT_CODE">{trip?.airportCode || '--'}</SimpleTableCell>
      <SimpleTableCell name="SERVICER_IATA_AIRLINE_CODE">{trip?.servicerIataAirlineCode || '--'}</SimpleTableCell>
      <SimpleTableCell name="FLIGHT_NUMBER">
        {trip?.name ||
          (trip?.flightNumber !== undefined && trip?.flightNumber !== null ? zeroPadFlightNumber(trip?.flightNumber || 0) : '') ||
          '--'}
      </SimpleTableCell>
      {format === InvoiceFormatEnum.Crew && <CrewIdCell />}
      <SimpleTableCell name="PILOTS">{trip?.pilots || 0}</SimpleTableCell>
      <SimpleTableCell name="ATTENDANTS">{trip?.attendants || 0}</SimpleTableCell>
      <SimpleTableCell name="PU_LOCATION">{trip?.puLocation || '--'}</SimpleTableCell>
      <SimpleTableCell name="DO_LOCATION">
        {subtotal !== undefined && !hasDiscounts ? 'SUBTOTAL:' : subtotal === undefined ? trip?.doLocation || '--' : ''}
      </SimpleTableCell>
      {hasDiscounts === 1 && (
        <SimpleTableCell name="SAVINGS">
          {subtotal === undefined ? (trip?.savings && trip.savings > 0 ? `$${formatDollars(trip?.savings)}` : '--') : 'SUBTOTAL:'}
        </SimpleTableCell>
      )}
      {subtotal === undefined ? (
        <RateCell id={trip.tripId} rate={trip.rate} selected={[]} onChange={setRow} />
      ) : (
        <SimpleTableCell name="RATE">{subtotal || '0.00'}</SimpleTableCell>
      )}
    </SimpleTableRow>
  );
};

export default forwardRef(InvoicePreviewRow);
